// src/features/files/pages/FilesPage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Folder as FolderIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  DriveFileMove as MoveIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { useFolders } from '../hooks/useFolders';
import { useFiles } from '../hooks/useFiles';
import { downloadFileLink } from '../hooks/useFileUrl';
import FileExplorer from '../components/FileExplorer';
import CreateFolderDialog from '../components/CreateFolderDialog';
import RenameFileDialog from '../components/RenameFileDialog';
import DeleteFilesDialog from '../components/DeleteFilesDialog';
import MoveFilesDialog from '../components/MoveFilesDialog';
import RenameFolderDialog from '../components/RenameFolderDialog';
import DeleteFolderDialog from '../components/DeleteFolderDialog';
import AccessDialog from '../components/AccessDialog';
import { getFolderName, getFolderLink, isOwnItem } from '../utils/getFolderName';
import { foldersAPI } from '../services/folders';
import { filesAPI } from '../services/files';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import { useFilePicker, useUploadQueue, UploadProgressList } from '../upload';
import { useFilePermissions } from '../../auth/hooks/usePermissions';

import PageHeader from '../../../components/common/PageHeader';

// Breadcrumb trail for `folderId`, walking up parent_folder_id. Used when the
// folder comes from the URL (reload, back/forward, shared link) rather than a click.
async function resolveBreadcrumbs(folderId, rootCrumb) {
  const trail = [];
  let id = folderId;
  while (id) {
    const res = await foldersAPI.getFolderDetails(id);
    const folder = res?.data?.data;
    if (!folder) break;
    trail.unshift({ id: folder.folder_id, name: getFolderName(folder.folder_name), raw: folder.folder_name });
    id = folder.parent_folder_id;
  }
  return [rootCrumb, ...trail];
}

export default function FilesPage() {
  const { t } = useTranslation(['files', 'common']);
  const [searchParams, setSearchParams] = useSearchParams();
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [secondarySelectedFile, setSecondarySelectedFile] = useState(null);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  // Files the open dialog acts on
  const [renaming, setRenaming] = useState(null);
  const [deleting, setDeleting] = useState([]);
  const [moving, setMoving] = useState([]);
  // Files ticked for acting on several at once
  const [checked, setChecked] = useState([]);
  // { kind: 'rename' | 'move' | 'delete', folder } for a folder's "⋯" menu
  const [folderDialog, setFolderDialog] = useState(null);

  const { canUpload, canDelete, canManage, canManageAccess } = useFilePermissions();
  const [accessTarget, setAccessTarget] = useState(null);
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();

  // The open folder lives in the URL (?folder=<id>) so reload, browser back/forward
  // and links all land in the right place. No param = department root.
  const currentFolderId = searchParams.get('folder') || null;

  const {
    loading,
    error,
    folderContents,
    navigateToFolder,
  } = useFolders();

  const { uploadFile } = useFiles();

  const refresh = useCallback(() => navigateToFolder(currentFolderId), [navigateToFolder, currentFolderId]);

  // Uploads go to the folder that was open when they were added; the list reloads
  // once when they're all done
  const uploads = useUploadQueue({
    upload: (file, { onUploadProgress, meta }) => uploadFile(file, {
      folder_id: meta.folderId,
      is_system_file: false,
      is_protected: true,
      onUploadProgress,
    }),
    onFinished: (results) => { if (results.length) refresh(); },
  });
  const { enqueue } = uploads;
  const addFiles = useCallback((files) => enqueue(files, { folderId: currentFolderId }), [enqueue, currentFolderId]);
  const picker = useFilePicker({ onFiles: addFiles });

  // Latest trail, readable from the effect below without re-running it
  const breadcrumbsRef = useRef(breadcrumbs);
  breadcrumbsRef.current = breadcrumbs;

  // Load contents + breadcrumbs whenever the folder in the URL changes
  useEffect(() => {
    navigateToFolder(currentFolderId);
    setChecked([]);

    // Clicked into a child or back to an ancestor: the trail is already known
    const known = breadcrumbsRef.current;
    const index = known.findIndex((b) => b.id === currentFolderId);
    if (index !== -1) {
      setBreadcrumbs(known.slice(0, index + 1));
      return undefined;
    }

    // Root name is translated at render time (see displayBreadcrumbs) so this
    // effect doesn't re-run — and refetch — when translations finish loading
    const rootCrumb = { id: null, name: null };
    if (!currentFolderId) {
      setBreadcrumbs([rootCrumb]);
      return undefined;
    }

    // Arrived from the URL (reload, back/forward, link): rebuild from the API
    let cancelled = false;
    resolveBreadcrumbs(currentFolderId, rootCrumb)
      .then((trail) => { if (!cancelled) setBreadcrumbs(trail); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentFolderId, navigateToFolder]);

  const openFolder = (folderId) => {
    setSearchParams(folderId ? { folder: folderId } : {});
  };

  // Navigate into a subfolder
  const handleFolderClick = (folder) => {
    if (folder.folder_id === currentFolderId) return;
    setBreadcrumbs((prev) => [
      ...prev,
      { id: folder.folder_id, name: getFolderName(folder.folder_name), raw: folder.folder_name },
    ]);
    openFolder(folder.folder_id);
  };

  // Navigate via breadcrumb
  const handleBreadcrumbClick = (breadcrumb) => {
    openFolder(breadcrumb.id);
  };

  // File actions, from the "⋯" menu and the preview
  const handleFileAction = async (action, file) => {
    switch (action) {
      case 'download':
        await downloadFileLink(file.file_id, { filename: file.filename });
        break;
      case 'rename':
        setRenaming(file);
        break;
      case 'move':
        setMoving([file]);
        break;
      case 'delete':
        setDeleting([file]);
        break;
      case 'access':
        setAccessTarget({ type: 'file', item: file });
        break;
      default:
        break;
    }
  };

  // Share a top-level folder with all departments, or stop sharing it
  const setShared = async (folder, shared) => {
    const name = getFolderName(folder.folder_name);
    try {
      await foldersAPI.updateFolder(folder.folder_id, { is_system_folder: shared });
      showSuccess(t(shared ? 'files:shared.share_done' : 'files:shared.unshare_done', { name }));
      refresh();
    } catch (err) {
      showError(err.response?.data?.message || t('common:errors.generic'));
    }
  };

  const handleFolderAction = (kind, folder) => {
    if (kind === 'access') {
      setAccessTarget({ type: 'folder', item: folder });
      return;
    }
    if (kind === 'share' || kind === 'unshare') {
      const shared = kind === 'share';
      const name = getFolderName(folder.folder_name);
      showConfirmDialog({
        title: t(shared ? 'files:shared.share_title' : 'files:shared.unshare_title', { name }),
        message: t(shared ? 'files:shared.share_hint' : 'files:shared.unshare_message'),
        confirmText: t(shared ? 'files:shared.share' : 'files:shared.unshare'),
        severity: shared ? 'info' : 'warning',
        onConfirm: () => setShared(folder, shared),
      });
      return;
    }
    setFolderDialog({ kind, folder });
  };

  // Renamed in place in the list; true once saved and the list shows the new name
  const handleRename = async (type, item, name) => {
    try {
      if (type === 'file') await filesAPI.updateFileMetadata(item.file_id, { filename: name });
      else await foldersAPI.updateFolder(item.folder_id, { folder_name: name });
      await refresh();
      return true;
    } catch (err) {
      showError(err.response?.data?.message || t('files:errors.rename_failed'));
      return false;
    }
  };

  // Selection change handler
  const handleSelectionChange = useCallback((files) => {
    setSelectedFile(files[0] || null);
    setSecondarySelectedFile(files[1] || null);
  }, []);

  const afterChange = () => {
    setChecked([]);
    refresh();
  };

  if (loading && !folderContents) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !folderContents) {
    // A folder the person may not see reads as missing; give them a way back
    return (
      <Box p={3}>
        <Alert
          severity="error"
          action={currentFolderId && (
            <Button color="inherit" size="small" onClick={() => { setBreadcrumbs([]); openFolder(null); }}>
              {t('files:back_to_all_files')}
            </Button>
          )}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  // The open folder's access rules may leave it read-only for this person
  const canAddHere = canUpload && folderContents?.data?.can_write !== false;

  // Root returns `folders`, folder contents return `subfolders`. Build a new
  // array — pushing into the response would duplicate entries on every render.
  const folders = [
    ...(folderContents?.data?.folders || []),
    ...(folderContents?.data?.subfolders || []),
  ];
  const files = folderContents?.data?.files || [];
  const displayBreadcrumbs = breadcrumbs.map((b) => (b.id === null ? { ...b, name: t('files:root') } : b));
  const currentFolderName = displayBreadcrumbs[displayBreadcrumbs.length - 1]?.name || t('files:root');

  // Files another department shares can only be looked at and downloaded here
  const ownFileActions = [
    'download',
    ...(canUpload ? ['rename', 'move'] : []),
    ...(canManageAccess ? ['access'] : []),
    ...(canDelete ? ['delete'] : []),
  ];
  // Access rules can leave a file read-only, or (with rules of its own) changeable
  // only by those who manage access: those actions are then left out
  const fileActions = (file) => {
    if (!isOwnItem(file)) return ['download'];
    if (file.can_modify === false) return ownFileActions.filter(a => !['rename', 'move', 'delete'].includes(a));
    return ownFileActions;
  };
  const folderMenu = [
    ...(canManage ? ['rename', 'move'] : []),
    ...(canDelete ? ['delete'] : []),
  ];
  // Only the department that manages the global files shares folders, and only
  // top-level ones (what's in a shared folder is shared with it)
  const managesGlobalFiles = !currentFolderId && Boolean(folderContents?.data?.manages_global_files);
  const shareAction = (folder) => {
    if (!canManage || currentFolderId) return null;
    if (folder.is_system_folder) return 'unshare';
    return managesGlobalFiles ? 'share' : null;
  };
  // Orbit's own folders can't be changed, nor can another department's shared ones;
  // the folders Orbit made for a piece of equipment, a room, a task … are found by
  // name, so they can only be deleted
  const parentFolderName = folderContents?.data?.folder_name;
  // Who may see a folder can be set on any of the department's own folders,
  // Orbit's too (e.g. the staff folder with contracts)
  const withAccess = (actions, folder) => {
    if (!canManageAccess || !isOwnItem(folder)) return actions;
    const i = actions.indexOf('delete');
    return i === -1 ? [...actions, 'access'] : [...actions.slice(0, i), 'access', ...actions.slice(i)];
  };
  // Folders follow the same: rules can leave a folder unchangeable for this person
  const baseFolderActions = (folder) => {
    const link = getFolderLink(folder, parentFolderName);
    if (link?.type === 'shared' && !link.own) return [];
    if (link?.type === 'system') return [];
    if (link?.type === 'linked') return folderMenu.filter(action => action === 'delete');
    const share = shareAction(folder);
    return share
      ? [...folderMenu.filter(action => action !== 'delete'), share, ...folderMenu.filter(action => action === 'delete')]
      : folderMenu;
  };
  const folderActions = folderMenu.length || canManageAccess
    ? (folder) => withAccess(folder.can_modify === false ? [] : baseFolderActions(folder), folder)
    : undefined;

  // In a shared folder: who sees what's added to it
  const openedFolder = folderContents?.data;
  const sharedNotice = currentFolderId && openedFolder?.is_system_folder && (
    <Alert severity="info" icon={<PublicIcon fontSize="inherit" />} sx={{ py: 0.25 }}>
      {isOwnItem(openedFolder)
        ? t('files:shared.notice_own')
        : t('files:shared.notice_other', {
          department: openedFolder.department_name || t('files:shared.managing_department'),
        })}
    </Alert>
  );

  const emptyState = (
    <Box display="flex" flexDirection="column" alignItems="center" gap={1} py={4} color="text.secondary">
      <FolderIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
      <Typography variant="body1">{t('files:empty_folder')}</Typography>
      {canUpload && (
        <>
          <Typography variant="body2">{t('files:upload.empty_hint')}</Typography>
          <Button variant="outlined" size="small" startIcon={<CloudUploadIcon />} onClick={picker.open} sx={{ mt: 1 }}>
            {t('files:upload.button')}
          </Button>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ height: 'calc(100vh - 125px)', display: 'flex', flexDirection: 'column' }}>
      <PageHeader
        icon={FolderIcon}
        title={t('files:page_title')}
        subtitle={t('files:page_subtitle')}
      />

      {/* FileExplorer */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <FileExplorer
          folders={folders}
          files={files}
          currentFolderId={currentFolderId}
          breadcrumbs={displayBreadcrumbs}
          layout="full"
          selectedFiles={[selectedFile, secondarySelectedFile].filter(Boolean)}
          onSelectionChange={handleSelectionChange}
          enableSearch={files.length > 0}
          enablePreview={true}
          enableViewToggle={true}
          enableCompareMode={true}
          enableBreadcrumbs={true}
          enableCreateFolder={canAddHere}
          enableUploadFile={canAddHere}
          fileActions={fileActions}
          folderActions={folderActions}
          onFolderAction={handleFolderAction}
          onRename={handleRename}
          parentFolderName={parentFolderName}
          checkable={(canUpload || canDelete) && files.some(isOwnItem)}
          checkedFileIds={checked.map(f => f.file_id)}
          onCheckedChange={setChecked}
          checkedActions={<>
            {canUpload && (
              <Button size="small" startIcon={<MoveIcon />} onClick={() => setMoving(checked)}>
                {t('files:actions.move_to')}
              </Button>
            )}
            {canDelete && (
              <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleting(checked)}>
                {t('files:actions.delete')}
              </Button>
            )}
          </>}
          emptyState={emptyState}
          notice={sharedNotice}
          onRefresh={refresh}
          onFolderNavigate={handleFolderClick}
          onBreadcrumbClick={handleBreadcrumbClick}
          onFileAction={handleFileAction}
          onFilesDropped={canAddHere ? addFiles : undefined}
          dropLabel={t('files:upload.drop_into', { folder: currentFolderName })}
          onCreateFolder={() => setCreateFolderOpen(true)}
          onUploadFile={picker.open}
        />
      </Box>
      {picker.input}

      <UploadProgressList
        variant="floating"
        items={uploads.items}
        onDismiss={uploads.dismiss}
        onClose={uploads.clearFinished}
      />

      {/* Loading Overlay */}
      {loading && folderContents && !uploads.items.length && (
        <Box
          position="fixed"
          bottom={16}
          right={16}
          bgcolor="background.paper"
          boxShadow={3}
          borderRadius={2}
          p={2}
          display="flex"
          alignItems="center"
          gap={2}
        >
          <CircularProgress size={24} />
          <Typography variant="body2">{t('common:messages.loading')}</Typography>
        </Box>
      )}

      {/* Dialogs */}
      <CreateFolderDialog
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        parentFolderId={currentFolderId}
        onSuccess={refresh}
        canShare={managesGlobalFiles && canManage}
      />
      <RenameFileDialog
        open={Boolean(renaming)}
        file={renaming}
        onClose={() => setRenaming(null)}
        onSuccess={refresh}
      />
      <DeleteFilesDialog
        open={deleting.length > 0}
        files={deleting}
        onClose={() => setDeleting([])}
        onDeleted={afterChange}
      />
      <MoveFilesDialog
        open={moving.length > 0}
        files={moving}
        startTrail={displayBreadcrumbs}
        onClose={() => setMoving([])}
        onMoved={afterChange}
      />
      <RenameFolderDialog
        open={folderDialog?.kind === 'rename'}
        folder={folderDialog?.folder}
        onClose={() => setFolderDialog(null)}
        onSuccess={refresh}
      />
      <MoveFilesDialog
        open={folderDialog?.kind === 'move'}
        folders={folderDialog ? [folderDialog.folder] : []}
        startTrail={displayBreadcrumbs}
        onClose={() => setFolderDialog(null)}
        onMoved={refresh}
      />
      <DeleteFolderDialog
        open={folderDialog?.kind === 'delete'}
        folder={folderDialog?.folder}
        onClose={() => setFolderDialog(null)}
        onDeleted={refresh}
      />
      <AccessDialog
        target={accessTarget}
        onClose={() => setAccessTarget(null)}
        onSaved={refresh}
      />
      <ConfirmDialog />
    </Box>
  );
}
